/**
 * The assistant's pointing finger: "here is the button".
 *
 * Stage 2 of the portal guide still writes nothing — it navigates, points and
 * prefills (`CONTRACT.md` §7). Pointing is this store plus
 * `GuideSpotlight.tsx`: whoever wants to direct the user to a control registers
 * its `data-guide-id`; the single component in `AppShell` looks for the
 * element, dims the rest and puts a ring around it.
 *
 * Deliberately **not a jotai atom**, even though `State.ts` otherwise sets the
 * house style — the same reasoning as for `chatHandoff.ts`: `index.tsx` mounts
 * `<Provider>` without its own store, so an atom would only live in the React
 * tree, but a spotlight is also registered from the message renderer and from
 * `useGuideNavigate`. Hence module state with observers, plus
 * `useSyncExternalStore` for the component.
 *
 * **A request replaces the previous one.** There is no queue: two rings at the
 * same time would be two pointing fingers, and a queue would point in sequence
 * at things nobody is asking about any more. The latest request wins.
 *
 * **Expiry.** A request whose target never shows up (the navigation was
 * aborted, the page only renders the button under a condition, the user
 * reloads) must not light up out of nowhere minutes later. It is therefore only
 * valid for `SPOTLIGHT_TTL_MS`. The age is checked on the read side, not by a
 * timer — the same shape as for the handover. The component's search deadline
 * (1.5 s) is shorter; the deadline here covers the case where no component is
 * mounted at all (login page, `/markup`).
 */

import { useSyncExternalStore } from 'react';

/** Deadline within which a request must be picked up. */
export const SPOTLIGHT_TTL_MS = 6_000;

export interface SpotlightRequest {
    /** Unique per registration — the basis of idempotent pickup. */
    id: string;
    /** `data-guide-id` of the target (`guideIds.ts`, registry field `guideId`). */
    guideId: string;
    /** Already translated: the store knows no language, the callers do. */
    label?: string;
    /** Short location hint („oben über der Tabelle"), also translated. */
    hint?: string;
    requestedAt: number;
    /** Custom deadline (tests, unusually long loading paths). */
    ttlMs: number;
}

let pending: SpotlightRequest | null = null;
let sequence = 0;
const listeners = new Set<() => void>();

function publish(next: SpotlightRequest | null): void {
    if (pending === next) return;
    pending = next;
    listeners.forEach((listener) => listener());
}

/**
 * Registers a target. A pending request is **replaced** in the process, not
 * queued.
 */
export function requestSpotlight(
    request: { guideId: string; label?: string; hint?: string; ttlMs?: number },
    now: number = Date.now(),
): SpotlightRequest {
    const spotlight: SpotlightRequest = {
        id: `spotlight-${now.toString(36)}-${(sequence += 1)}`,
        guideId: request.guideId,
        label: request.label,
        hint: request.hint,
        requestedAt: now,
        ttlMs: request.ttlMs ?? SPOTLIGHT_TTL_MS,
    };
    publish(spotlight);
    return spotlight;
}

/** True as long as the request is within its deadline. */
export function isSpotlightFresh(
    request: SpotlightRequest | null,
    now: number = Date.now(),
): boolean {
    if (!request) return false;
    const age = now - request.requestedAt;
    return age >= 0 && age < request.ttlMs;
}

/** The pending request, if it is still valid — without consuming it. */
export function peekSpotlight(now: number = Date.now()): SpotlightRequest | null {
    return isSpotlightFresh(pending, now) ? pending : null;
}

/**
 * Removes the pointing finger: end of the demonstration, Escape, a click
 * elsewhere — and the case where the target has not shown up after the search
 * deadline. Also the cleanup path for tests.
 */
export function clearSpotlight(): void {
    publish(null);
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Deliberately the **raw** module state, not the freshness check: a
 * `getSnapshot` that reads the clock would return different values within the
 * same render and send React into an infinite loop (see `chatHandoff.ts`).
 * Expiry is decided by the component in its effect, with `isSpotlightFresh`.
 */
function snapshot(): SpotlightRequest | null {
    return pending;
}

export function useSpotlight(): SpotlightRequest | null {
    return useSyncExternalStore(subscribe, snapshot, snapshot);
}
